import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { finalize, map, take } from 'rxjs';
import { IntegracaoParticipanteStatus } from '../../core/enum/integracao-participante-status.enum';
import { RelatorioFormato } from '../../core/enum/relatorio-formato.enum';
import {
  AdesaoIntegracao,
  AdesaoIntegracaoFiltros,
  AdesaoLog,
  IntegracaoLog,
} from '../../core/models/integracao.model';
import { Campanha } from '../../core/models/campanha.model';
import { CampanhaService } from '../../core/services/campanha.service';
import { IntegracaoService } from '../../core/services/integracao.service';
import { ToastService } from '../../core/services/toast.service';
import { formatDateIsoToBr } from '../../core/utils/date-format.util';
import { toLocalIsoDate } from '../../core/validators/campanha-vigencia.validator';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { LoaderService } from '../../shared/services/loader/loader.service';
import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';
import { DataTableComponent } from '../../shared/components/data-table/data-table.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { AdesaoIntegracaoFilterBarComponent } from './adesao-integracao-filter-bar.component';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { IntegracaoLogDialogComponent } from './integracao-log-dialog.component';
import { AdesaoLogDialogComponent } from './adesao-log-dialog.component';
import { IntegracaoResumoCargaComponent } from './integracao-resumo-carga.component';
import { DeParaConfigModalComponent } from './de-para-config-modal.component';
import {
  ActionsMenuPosition,
  adjustActionsMenuPosition,
  getActionsMenuPosition,
} from '../../shared/utils/actions-menu.util';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-integracao-gestao',
  imports: [
    BackButtonComponent,
    PageHeaderComponent,
    AdesaoIntegracaoFilterBarComponent,
    DataTableComponent,
    StatusBadgeComponent,
    EmptyStateComponent,
    PaginationComponent,
    AlertDialogComponent,
    IntegracaoLogDialogComponent,
    AdesaoLogDialogComponent,
    IntegracaoResumoCargaComponent,
    DeParaConfigModalComponent,
  ],
  templateUrl: './integracao-gestao.component.html',
  styleUrl: './integracao-gestao.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntegracaoGestaoComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly campanhaService = inject(CampanhaService);
  private readonly integracaoService = inject(IntegracaoService);
  private readonly toastService = inject(ToastService);
  private readonly loaderService = inject(LoaderService);
  private readonly injector = inject(Injector);

  readonly integracaoId = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('id') ?? '')),
    { initialValue: '' },
  );

  private readonly campanhaSignal = signal<Campanha | undefined>(undefined);
  private readonly adesoesSignal = signal<readonly AdesaoIntegracao[]>([]);
  private readonly filtrosSignal = signal<AdesaoIntegracaoFiltros>({});

  readonly campanha = this.campanhaSignal.asReadonly();
  readonly adesoes = this.adesoesSignal.asReadonly();
  readonly currentPage = signal(1);
  readonly pageSize = PAGE_SIZE;

  readonly filtroAplicado = computed(() => {
    const { participante, status, data } = this.filtrosSignal();
    return Boolean(participante || status || data);
  });

  /** O ID abre o título porque é por ele que a campanha é citada em chamados e planilhas. */
  readonly tituloCampanha = computed(() => {
    const campanha = this.campanha();
    if (!campanha) {
      return '';
    }

    return campanha.id ? `${campanha.id} - ${campanha.nome}` : campanha.nome;
  });

  /** Vigência da campanha. Sem `dtaFim`, a campanha não tem data para encerrar. */
  readonly periodoCampanha = computed(() => {
    const campanha = this.campanha();
    if (!campanha?.dataInicio) {
      return '';
    }

    const inicio = formatDateIsoToBr(campanha.dataInicio);
    if (!campanha.dataFim) {
      return `a partir de ${inicio}`;
    }

    return `${inicio} – ${formatDateIsoToBr(campanha.dataFim)}`;
  });

  /** Quando a troca de perfil passa a valer no portal do participante (`dtIniVigPartic`). */
  readonly inicioVigencia = computed(() => {
    const data = this.campanha()?.dataInicioVigencia;
    return data ? formatDateIsoToBr(data) : '';
  });

  /**
   * Quando a carga foi integrada e quem disparou, numa frase só. É contexto da tela, não campo de
   * formulário — daí o texto corrido em vez de um par rótulo/valor.
   *
   * O responsável sai como "Operador {id}": `GET /v1/Campanha/{id}` devolve o identificador do
   * operador, não o nome. Quando o back publicar o nome, só esta linha muda.
   */
  readonly proveniencia = computed(() => {
    const campanha = this.campanha();
    if (!campanha) {
      return '';
    }

    const quando = campanha.dataIntegracao
      ? [formatDateIsoToBr(campanha.dataIntegracao), campanha.horaIntegracao]
          .filter(Boolean)
          .join(' às ')
      : '';
    const responsavel =
      campanha.idOperadorIntegracao != null ? `Operador ${campanha.idOperadorIntegracao}` : '';

    if (quando && responsavel) {
      return `Integrada em ${quando} por ${responsavel}`;
    }

    if (quando) {
      return `Integrada em ${quando}`;
    }

    return responsavel ? `Integrada por ${responsavel}` : '';
  });

  readonly adesoesPaginadas = computed(() => {
    const inicio = (this.currentPage() - 1) * PAGE_SIZE;
    return this.adesoesSignal().slice(inicio, inicio + PAGE_SIZE);
  });

  readonly aguardandoLiberacaoCount = computed(() => this.aguardandoLiberacaoIds().length);

  private readonly selecionadosSignal = signal<ReadonlySet<number>>(new Set());
  readonly selecionados = this.selecionadosSignal.asReadonly();
  readonly liberando = signal(false);
  readonly mostrarConfirmacaoLiberar = signal(false);

  readonly RelatorioFormato = RelatorioFormato;
  readonly relatoriosMenuAberto = signal(false);
  readonly baixandoRelatorio = signal(false);

  readonly deParaModalAberto = signal(false);
  readonly idCampanhaNumero = computed(() => Number.parseInt(this.integracaoId(), 10));

  readonly menuAbertoId = signal<number | null>(null);
  readonly menuPosition = signal<ActionsMenuPosition | null>(null);

  private readonly logSignal = signal<IntegracaoLog | undefined>(undefined);
  readonly log = this.logSignal.asReadonly();

  /** Resumo fixo no topo da tela; o diálogo mostra o mesmo bloco junto dos registros. */
  private readonly resumoCargaSignal = signal<IntegracaoLog | undefined>(undefined);
  readonly resumoCarga = this.resumoCargaSignal.asReadonly();

  /**
  * Carga inteiramente integrada: não sobra nada para filtrar nem para liberar. Vem do resumo, e
  * não da lista carregada, porque a lista muda com o filtro e o resumo descreve a carga toda.
  */
  readonly cargaIntegrada = computed(() => {
    const resumo = this.resumoCarga();
    return Boolean(resumo && resumo.total > 0 && resumo.integrados === resumo.total);
  });

  private readonly logAdesaoSignal = signal<AdesaoLog | undefined>(undefined);
  readonly logAdesao = this.logAdesaoSignal.asReadonly();

  /**
   * Só quem ainda está "Aguardando" pode ser liberado; sucesso e falha já foram processados.
   * A linha é identificada por `idPerfilInvestimentoParticipante`, não por `id`: o `idPerfilSelPart`
   * repete entre participantes e faria seleção e menu de ações agirem sobre a linha errada.
   */
  readonly aguardandoLiberacaoIds = computed(() =>
    this.adesoesSignal()
      .filter((adesao) => adesao.status === IntegracaoParticipanteStatus.Aguardando)
      .map((adesao) => adesao.idPerfilInvestimentoParticipante),
  );

  readonly todosSelecionados = computed(() => {
    const elegiveis = this.aguardandoLiberacaoIds();
    return elegiveis.length > 0 && elegiveis.every((id) => this.selecionadosSignal().has(id));
  });

  constructor() {
    this.carregarCampanha();
    this.carregarResumoCarga();
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  toggleRelatoriosMenu(event: Event): void {
    event.stopPropagation();
    this.relatoriosMenuAberto.update((aberto) => !aberto);
  }

  /** Baixa o relatório da campanha no formato escolhido no menu. */
  onRelatorio(formato: RelatorioFormato): void {
    this.relatoriosMenuAberto.set(false);

    const id = this.integracaoId();
    if (!id || this.baixandoRelatorio()) {
      return;
    }

    // O loader global é o feedback de espera da tela; o rótulo do botão fica parado para a
    // barra de ações não mudar de largura no meio do clique.
    this.baixandoRelatorio.set(true);
    this.loaderService.showLoader();

    this.campanhaService
      .exportarRelatorio(id, formato)
      .pipe(
        take(1),
        finalize(() => {
          this.baixandoRelatorio.set(false);
          this.loaderService.hideLoader();
        }),
      )
      .subscribe({
        next: (arquivo) => this.baixarArquivo(arquivo, this.nomeDoRelatorio(formato)),
        error: (error: unknown) => {
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível gerar o relatório.';
          this.toastService.show(message, 'error');
        },
      });
  }

  /** Id e data no nome: quem baixa o relatório mais de uma vez não fica com "(1)" no arquivo. */
  private nomeDoRelatorio(formato: RelatorioFormato): string {
    const extensao = formato === RelatorioFormato.Pdf ? 'pdf' : 'xlsx';
    return `relatorio-integracao-${this.integracaoId()}-${toLocalIsoDate(new Date())}.${extensao}`;
  }

  /** Dispara o download de um Blob via link temporário. */
  private baixarArquivo(blob: Blob, nomeArquivo: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = nomeArquivo;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Libera a memória do Blob; o download já foi iniciado pelo clique acima.
    URL.revokeObjectURL(url);
  }

  onAbrirDePara(): void {
    this.deParaModalAberto.set(true);
  }

  onFecharDePara(): void {
    this.deParaModalAberto.set(false);
  }

  onLogClick(): void {
    const id = this.integracaoId();
    if (!id) {
      return;
    }

    this.integracaoService.obterLog(id).subscribe({
      next: (log) => this.logSignal.set(log),
      error: (error: unknown) => {
        const message =
          error instanceof ApiHttpError
            ? error.message
            : 'Não foi possível carregar o log de integração.';
        this.toastService.show(message, 'error');
      },
    });
  }

  onFecharLog(): void {
    this.logSignal.set(undefined);
  }

  /** Menu de ações da linha (⋮), hoje com uma única opção: "Visualizar log". */
  toggleMenu(id: number, event: Event): void {
    event.stopPropagation();

    if (this.menuAbertoId() === id) {
      this.fecharMenu();
      return;
    }

    this.menuAbertoId.set(id);
    this.menuPosition.set(getActionsMenuPosition(event));
    this.ajustarPosicaoMenu();
  }

  onVisualizarLog(adesao: AdesaoIntegracao, event: Event): void {
    event.stopPropagation();
    this.fecharMenu();

    const idCampanha = this.integracaoId();
    if (!idCampanha) {
      return;
    }

    this.integracaoService.obterLogAdesao(idCampanha, adesao).subscribe({
      next: (log) => this.logAdesaoSignal.set(log),
      error: (error: unknown) => {
        const message =
          error instanceof ApiHttpError
            ? error.message
            : 'Não foi possível carregar o log do participante.';
        this.toastService.show(message, 'error');
      },
    });
  }

  onFecharLogAdesao(): void {
    this.logAdesaoSignal.set(undefined);
  }

  @HostListener('document:click')
  fecharMenu(): void {
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);
    this.relatoriosMenuAberto.set(false);
  }

  private ajustarPosicaoMenu(): void {
    afterNextRender(
      () => {
        const ajustada = adjustActionsMenuPosition(this.menuPosition());
        if (ajustada) {
          this.menuPosition.set(ajustada);
        }
      },
      { injector: this.injector },
    );
  }

  onFiltersChange(filtros: AdesaoIntegracaoFiltros): void {
    this.filtrosSignal.set(filtros);
    this.currentPage.set(1);
    this.carregarAdesoes();
  }

  isSelecionavel(adesao: AdesaoIntegracao): boolean {
    return adesao.status === IntegracaoParticipanteStatus.Aguardando || adesao.status === IntegracaoParticipanteStatus.Erro;
  }

  toggleSelecao(id: number): void {
    this.selecionadosSignal.update((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) {
        proximo.delete(id);
      } else {
        proximo.add(id);
      }
      return proximo;
    });
  }

  toggleSelecionarTodos(): void {
    this.selecionadosSignal.set(this.todosSelecionados() ? new Set() : new Set(this.aguardandoLiberacaoIds()));
  }

  onCancelarSelecao(): void {
    this.selecionadosSignal.set(new Set());
  }

  onLiberarClick(): void {
    if (this.selecionadosSignal().size === 0 || this.liberando()) {
      return;
    }
    this.mostrarConfirmacaoLiberar.set(true);
  }

  onCancelarConfirmacaoLiberar(): void {
    this.mostrarConfirmacaoLiberar.set(false);
  }

  onConfirmarLiberar(): void {
    this.mostrarConfirmacaoLiberar.set(false);

    const id = this.integracaoId();
    const selecionados = this.adesoesSignal().filter((adesao) =>
      this.selecionadosSignal().has(adesao.idPerfilInvestimentoParticipante),
    );
    if (!id || selecionados.length === 0 || this.liberando()) {
      return;
    }

    this.liberando.set(true);

    this.integracaoService
      .liberarAdesoes(id, selecionados)
      .pipe(finalize(() => this.liberando.set(false)))
      .subscribe({
        next: () => {
          this.toastService.show('Integração liberada com sucesso.', 'success');
          this.selecionadosSignal.set(new Set());
          this.carregarAdesoes();
          // O resumo também envelhece na liberação: sem recarregá-lo, os totais do topo ficam
          // nos valores de antes e a mensagem de carga concluída só apareceria com um F5.
          this.carregarResumoCarga();
        },
        error: (error: unknown) => {
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível liberar as adesões selecionadas.';
          this.toastService.show(message, 'error');
        },
      });
  }

  /** O id da rota é o da campanha escolhida na tela de seleção. */
  /** Erro aqui não vira toast: o resumo é complementar e a lista de adesões é o essencial. */
  private carregarResumoCarga(): void {
    const id = this.integracaoId();
    if (!id) {
      return;
    }

    this.integracaoService.obterLog(id).subscribe({
      next: (log) => this.resumoCargaSignal.set(log),
      error: () => this.resumoCargaSignal.set(undefined),
    });
  }

  private carregarCampanha(): void {
    const id = this.integracaoId();
    if (!id) {
      return;
    }

    this.campanhaService.obterCampanha(id).subscribe({
      next: (campanha) => this.campanhaSignal.set(campanha),
      error: () => this.campanhaSignal.set(undefined),
    });
  }

  private carregarAdesoes(): void {
    const id = this.integracaoId();
    if (!id) {
      return;
    }

    this.loaderService.showLoader();

    this.integracaoService
      .listarAdesoes(id, this.filtrosSignal())
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe({
        next: (lista) => this.adesoesSignal.set(lista),
        error: (error: unknown) => {
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível carregar as adesões da integração.';
          this.toastService.show(message, 'error');
        },
      });
  }
}
